import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

export type OverlayState = "connectivity_lost" | "session_expired" | null;

// Imperative bridge so code without access to the hook (Apollo links, legacy UserContext, auth
// gates) can read/trigger the expired state. Inert until a controller mounts.
let sessionExpiredLatched = false;
let triggerSessionExpiredImpl: (() => void) | null = null;

export const isSessionExpired = (): boolean => sessionExpiredLatched;
export const triggerSessionExpired = (): void => {
  triggerSessionExpiredImpl?.();
};

export interface OverlayControl {
  reportConnectivityLost: () => void;
  reportConnectivityRestored: () => void;
  reportSessionExpired: () => void;
  activeOverlay: OverlayState;
  sessionExpired: boolean;
}

const OverlayControlContext = createContext<OverlayControl | null>(null);

export const useOverlayControl = (): OverlayControl => {
  const ctx = useContext(OverlayControlContext);
  if (ctx === null) {
    throw new Error("useOverlayControl must be used within AppOverlayController");
  }
  return ctx;
};

interface Props {
  children: React.ReactNode;
}

// Manages which full-screen overlay shows, with connectivity_lost taking priority: if the session
// expires while offline, session_expired is shown once connectivity returns — avoiding a confusing
// "sign in" prompt when the real problem is the network.
//
// `sessionExpired` is the teardown/purge signal (flips the moment expiry is known, regardless of
// display priority); `activeOverlay` only drives what's shown. sessionExpiredLatched mirrors it
// synchronously for non-React readers.
export const AppOverlayController: React.FC<Props> = ({ children }) => {
  const [activeOverlay, setActiveOverlay] = useState<OverlayState>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  // Ref keeps overlay state readable synchronously inside callbacks without stale closure
  const activeOverlayRef = useRef<OverlayState>(null);

  const setOverlay = useCallback((state: OverlayState): void => {
    activeOverlayRef.current = state;
    setActiveOverlay(state);
  }, []);

  const reportConnectivityLost = useCallback((): void => {
    setOverlay("connectivity_lost");
  }, [setOverlay]);

  const reportConnectivityRestored = useCallback((): void => {
    // Fires on every healthy heartbeat, not just recovery — must not clear a session_expired overlay.
    if (activeOverlayRef.current === "session_expired") {
      return;
    }
    setOverlay(sessionExpiredLatched ? "session_expired" : null);
  }, [setOverlay]);

  const reportSessionExpired = useCallback((): void => {
    sessionExpiredLatched = true;
    setSessionExpired(true);
    // Defer only the overlay when connectivity_lost has priority; expiry state is already set.
    if (activeOverlayRef.current !== "connectivity_lost") {
      setOverlay("session_expired");
    }
  }, [setOverlay]);

  // Register the bridge and reset the latch for a fresh app instance.
  useEffect(() => {
    sessionExpiredLatched = false;
    triggerSessionExpiredImpl = reportSessionExpired;
    return (): void => {
      triggerSessionExpiredImpl = null;
    };
  }, [reportSessionExpired]);

  const value = useMemo<OverlayControl>(
    () => ({
      reportConnectivityLost,
      reportConnectivityRestored,
      reportSessionExpired,
      activeOverlay,
      sessionExpired,
    }),
    [reportConnectivityLost, reportConnectivityRestored, reportSessionExpired, activeOverlay, sessionExpired]
  );

  return <OverlayControlContext.Provider value={value}>{children}</OverlayControlContext.Provider>;
};
